import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { normalizeApiFocusAreas } from "../../../utils/helpers/focusAreas";
import {
  COUNTED_OBSERVATION_LIMIT,
  COUNTED_WINDOW_DAYS,
  computeStandardScoresFromDetails,
  selectCountedObservations,
} from "../../../utils/helpers/scoring";
import { scoreTier } from "../../../utils/helpers/scoreColors";
import { formatMonthDay } from "../../../utils/helpers/dateDisplay";
import {
  answerTo,
  anyAnswerTo,
  lessonStatusOn,
  notScoredTitle,
  standardScopeLabel,
  standardsForTeacher,
} from "../../../utils/helpers/standardScope";
import {
  getFocusAreas,
  getPeerExperts,
} from "../../../services/observationService";
import DonutProgress from "../../../components/DonutProgress/DonutProgress";
import Spinner from "../../../components/Spinner/Spinner";
import RecommendedMaterials from "../../Materials/RecommendedMaterials";
import PeerExpertsRail from "./PeerExpertsRail";

const tierLabelKey = (score) => {
  if (score >= 80) return "teacher.tierExemplary";
  if (score >= 60) return "teacher.tierProficient";
  return "teacher.tierDeveloping";
};

const tierBadgeClass = (score) =>
  scoreTier(score)?.badgeClass || "bg-slate-100 text-slate-500";

// The `yes_no_na` AnswerOptionSet values the BIL rubric ships with — used to
// pick a tag style for "Recent Question Outcomes" without coupling to the
// option's mutable label.
const OPTION_VALUE = { YES: "yes", NO: "no" };


export default function PerformanceOverviewTab({
  user,
  rubric,
  observationDetails,
  expandedStandardId,
  setExpandedStandardId,
  showFocusAreas,
  setShowFocusAreas,
  onRequestVisit,
}) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  // YES / NO / Not observed (or raw label) tag for one answer snapshot.
  const tagFor = (ans) => {
    const label = ans?.selected_option_label_snapshot;
    const value = ans?.selected_option_value_snapshot;
    if (!ans || !label)
      return {
        text: "—",
        title: t("common.notObserved"),
        cls: "bg-slate-100 text-slate-500",
      };
    if (value === OPTION_VALUE.YES)
      return { text: t("common.yes"), cls: "bg-emerald-100 text-emerald-700" };
    if (value === OPTION_VALUE.NO)
      return { text: t("common.no"), cls: "bg-rose-100 text-rose-700" };
    return { text: label, cls: "bg-slate-100 text-slate-600" };
  };
  // A lesson not scored on the standard: nothing to observe, unlike "—";
  // the title says why.
  const notScoredTag = (status) => ({
    text: "–",
    title: notScoredTitle(t, status),
    cls: "border border-dashed border-slate-200 text-slate-300",
  });
  const [apiFocus, setApiFocus] = useState(null);
  const [focusLoading, setFocusLoading] = useState(true);
  const [peerExperts, setPeerExperts] = useState([]);

  useEffect(() => {
    if (!user?.uuid) return undefined;
    const controller = new AbortController();
    setFocusLoading(true);
    getFocusAreas({
      params: { teacher: user.uuid },
      signal: controller.signal,
    })
      .then((data) => setApiFocus(data))
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        setApiFocus(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setFocusLoading(false);
      });
    return () => controller.abort();
  }, [user?.uuid]);

  const focusAreas = useMemo(
    () => normalizeApiFocusAreas(apiFocus),
    [apiFocus],
  );

  // Same counted window + recency weighting as the teacher overview modal —
  // a standard's % must read identically for the teacher and their manager.
  const standardScores = useMemo(
    () => computeStandardScoresFromDetails(observationDetails, rubric?.standards),
    [observationDetails, rubric],
  );

  // The teacher's own subjects decide which subject-scoped standards earn a
  // card here (see standardsForTeacher), across every school they teach at.
  const subjectUuids = useMemo(
    () => (user?.organizations || []).flatMap((o) => (o.subjects || []).map((s) => s.uuid)),
    [user?.organizations],
  );
  const standards = useMemo(
    () =>
      standardsForTeacher(
        [...(rubric?.standards || [])].sort((a, b) => a.order - b.order),
        subjectUuids,
        standardScores,
      ),
    [rubric, subjectUuids, standardScores],
  );

  const weakestStandards = useMemo(
    () =>
      standards
        .map((s) => ({ ...s, score: standardScores[s.uuid] }))
        .filter((s) => s.score != null)
        .sort((a, b) => a.score - b.score)
        .slice(0, 3),
    [standards, standardScores],
  );

  const peerStandardsKey = useMemo(
    () => weakestStandards.map((s) => s.uuid).sort().join(","),
    [weakestStandards],
  );

  useEffect(() => {
    if (!peerStandardsKey) {
      setPeerExperts([]);
      return undefined;
    }
    const controller = new AbortController();
    getPeerExperts({
      params: { standards: peerStandardsKey },
      signal: controller.signal,
    })
      .then((data) => setPeerExperts(Array.isArray(data) ? data : []))
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        setPeerExperts([]);
      });
    return () => controller.abort();
  }, [peerStandardsKey]);

  const insufficientFocusData = !!apiFocus?.insufficient_data;
  let focusHeadline;
  if (focusLoading) {
    focusHeadline = t("teacher.focusAnalyzing");
  } else if (insufficientFocusData) {
    focusHeadline = t("teacher.focusInsufficient");
  } else {
    focusHeadline = t("teacher.focusCount", { count: focusAreas.length });
  }
  const focusEmptyMessage = insufficientFocusData
    ? t("teacher.focusEmptyInsufficient")
    : t("teacher.focusEmptyNone");

  return (
    // `items-start` must not apply while this is a COLUMN flex: it is the
    // cross-axis rule there, so every child sized to its content instead of the
    // screen and long text ran off the right edge with nothing to scroll.
    <div className="flex flex-col lg:flex-row gap-12 lg:items-start">
      <div className="flex-1 min-w-0 w-full space-y-10">
      <div className="bg-indigo-50/30 border border-indigo-100/50 rounded-[40px] p-8 flex items-start gap-6 shadow-sm">
        <div className="w-14 h-14 bg-white text-indigo-600 rounded-2xl flex items-center justify-center shrink-0 shadow-md border border-indigo-50">
          <svg aria-hidden="true" className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-2 h-2 bg-indigo-500 rounded-full animate-pulse motion-reduce:animate-none" />
            <p className="text-[11px] font-black text-indigo-700 uppercase tracking-[0.2em]">
              {t("teacher.performanceIntelligence")}
            </p>
          </div>
          <p className="text-sm text-indigo-900/70 font-medium leading-relaxed">
            {t("teacher.performanceIntelligenceBody", {
              count: COUNTED_OBSERVATION_LIMIT,
              days: COUNTED_WINDOW_DAYS,
            })}
          </p>
        </div>
      </div>

      <div className="bg-white border-2 border-slate-100 rounded-[32px] overflow-hidden shadow-sm">
        <button
          onClick={() => setShowFocusAreas(!showFocusAreas)}
          aria-expanded={showFocusAreas}
          aria-controls="suggested-focus-panel"
          className="w-full p-6 flex items-center justify-between hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500"
        >
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center shrink-0 border-4 border-white shadow-inner">
              <svg aria-hidden="true" className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div className="text-left">
              <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">
                {t("teacher.suggestedFocusAreas")}
              </p>
              <p className="text-sm font-black text-slate-900 leading-tight" aria-live="polite">
                {focusHeadline}
              </p>
            </div>
          </div>
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition ${
              showFocusAreas ? "bg-indigo-600 text-white rotate-180" : "bg-slate-100 text-slate-400"
            }`}
          >
            <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        <AnimatePresence>
          {showFocusAreas && (
            <motion.div
              id="suggested-focus-panel"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.3, ease: "easeInOut" }}
              className="overflow-hidden border-t border-slate-100 bg-slate-50/30"
            >
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {focusAreas.length > 0 ? (
                  focusAreas.map((area) => (
                    <div
                      key={area.key}
                      className="bg-white border border-slate-200 rounded-2xl p-3.5 flex items-start gap-3.5 shadow-sm hover:border-indigo-200"
                    >
                      <div className="w-9 h-9 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-center shrink-0 text-[9px] font-black text-slate-500">
                        S{area.domainNumber}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span
                            className="text-[9px] font-black text-indigo-600 uppercase tracking-widest truncate min-w-0 flex-1"
                            title={area.domainTitle}
                          >
                            {area.domainTitle}
                          </span>
                          <span
                            className="shrink-0 text-[10px] font-black text-rose-500 bg-rose-50 px-2 py-0.5 rounded-full tabular-nums"
                            title={t("teacher.notObservedTimes", { count: area.count })}
                            aria-label={t("teacher.notObservedTimes", { count: area.count })}
                          >
                            ×{area.count}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-600 leading-snug font-medium line-clamp-2">
                          {area.fullText}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-10 text-center col-span-2">
                    <p className="text-xs text-slate-500 font-bold">
                      {focusEmptyMessage}
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Loading means no rubric yet: the filtered list can be empty for good. */}
      {!rubric ? (
        <Spinner label={t("teacher.loadingRubric")} size="sm" className="py-12" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {standards.map((std) => {
            const rawScore = standardScores[std.uuid];
            const score = rawScore == null ? null : Math.round(rawScore);
            const isExpanded = expandedStandardId === std.uuid;
            const scopeLabel = standardScopeLabel(t, std);

            return (
              <div
                key={std.uuid}
                className={`bg-white border transition-all duration-500 rounded-[32px] overflow-hidden flex flex-col ${
                  isExpanded
                    ? "border-indigo-400 shadow-2xl ring-8 ring-indigo-50/50 md:col-span-2"
                    : "border-slate-100 hover:border-indigo-200 hover:shadow-xl hover:-translate-y-1 shadow-sm"
                }`}
              >
                <button
                  onClick={() => setExpandedStandardId(isExpanded ? null : std.uuid)}
                  className="w-full text-left p-6"
                >
                  <div className="flex items-center gap-5">
                    <DonutProgress
                      score={score}
                      size={isExpanded ? 64 : 52}
                      strokeWidth={isExpanded ? 6 : 5}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                          {t("teacher.standardN", { number: std.number || std.order })}
                        </span>
                        {isExpanded && score != null && (
                          <span
                            className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-tighter ${tierBadgeClass(score)}`}
                          >
                            {t(tierLabelKey(score))}
                          </span>
                        )}
                        {score == null && (
                          <span className="px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-tighter bg-slate-100 text-slate-500">
                            {t("teacher.noData")}
                          </span>
                        )}
                      </div>
                      <h4
                        className={`font-black text-slate-800 leading-tight tracking-tight ${
                          isExpanded ? "text-xl" : "text-sm"
                        }`}
                      >
                        {std.title}
                      </h4>
                      {scopeLabel && (
                        <p
                          className="mt-1 text-[10px] font-semibold text-indigo-600 truncate"
                          title={scopeLabel}
                        >
                          {scopeLabel}
                        </p>
                      )}
                    </div>
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isExpanded ? "bg-indigo-600 text-white rotate-180" : "bg-slate-50 text-slate-400"
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-6 pb-6 pt-2 border-t border-slate-100">
                    {(() => {
                      // All fetched details (counted window + one older for
                      // context), oldest → newest; columns outside the counted
                      // window render dimmed — same affordance as the teacher
                      // overview modal.
                      const countedUuids = new Set(
                        selectCountedObservations(observationDetails).map(
                          (o) => o.uuid,
                        ),
                      );
                      const columns = [...observationDetails].reverse();
                      const orderedQuestions = [...(std.questions || [])]
                        .filter((q) => !q.parent_question)
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                      const matchedAny = anyAnswerTo(columns, std, orderedQuestions);
                      if (orderedQuestions.length === 0) {
                        return (
                          <p className="text-xs text-slate-400 font-medium pt-1">
                            {t("teacher.noRecentForStandard")}
                          </p>
                        );
                      }
                      // Questions show before any observation answers them;
                      // the answer columns appear once one does.
                      const shownColumns = matchedAny ? columns : [];
                      // Each lesson's status on the standard, read once for
                      // every question.
                      const statuses = shownColumns.map((d) =>
                        lessonStatusOn(d, std, rubric.uuid),
                      );
                      return (
                        <div className="pt-1">
                          {!matchedAny && (
                            <p className="text-xs text-slate-400 font-medium mb-3 px-1">
                              {t("teacher.noRecentForStandard")}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mb-2 px-1">
                            <span className="flex-1 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                              {t("teacherModal.criteria")}
                            </span>
                            <div className="flex gap-3 shrink-0">
                              {shownColumns.map((d) => (
                                <span
                                  key={d.uuid}
                                  className={`w-16 text-center text-[9px] font-bold text-slate-500 uppercase tracking-wider ${countedUuids.has(d.uuid) ? "" : "opacity-40"}`}
                                  title={
                                    countedUuids.has(d.uuid)
                                      ? undefined
                                      : t("teacherModal.notCounted")
                                  }
                                >
                                  {formatMonthDay(d.observed_at)}
                                </span>
                              ))}
                            </div>
                          </div>
                          <ul className="space-y-1.5">
                            {orderedQuestions.map((q) => (
                              <li
                                key={q.uuid}
                                className="flex justify-between items-start gap-4 p-3 rounded-lg bg-slate-50/50 border border-slate-100"
                              >
                                <div className="flex items-start gap-3 min-w-0 flex-1">
                                  <span className="font-mono text-[9px] bg-white text-slate-500 rounded px-1.5 py-0.5 border border-slate-200 shrink-0 mt-0.5">
                                    {q.short_label || q.code}
                                  </span>
                                  <span className="text-xs text-slate-700 break-words leading-snug">
                                    {q.text}
                                  </span>
                                </div>
                                <div className="flex gap-3 items-start shrink-0">
                                  {shownColumns.map((d, i) => {
                                    const tag = statuses[i] === "scored"
                                      ? tagFor(answerTo(d, q))
                                      : notScoredTag(statuses[i]);
                                    return (
                                      <div
                                        key={d.uuid}
                                        className={`w-16 flex justify-center ${countedUuids.has(d.uuid) ? "" : "opacity-40"}`}
                                        title={
                                          countedUuids.has(d.uuid)
                                            ? undefined
                                            : t("teacherModal.notCounted")
                                        }
                                      >
                                        <span
                                          className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md ${tag.cls}`}
                                          title={tag.title}
                                        >
                                          {tag.text}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="pt-8 border-t border-slate-200">
        <h3 className="text-xl font-black text-slate-900 tracking-tight mb-1">
          {t("materials.recommendedHeading")}
        </h3>
        <p className="text-xs text-slate-500 font-medium mb-6">
          {t("teacher.pdSubtitle")}
        </p>
        <RecommendedMaterials
          user={user}
          gridClassName="grid grid-cols-1 md:grid-cols-2 gap-6"
          loadingFallback={
            <Spinner label={t("materials.loading")} size="sm" className="py-10" />
          }
          emptyFallback={
            <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white/50 py-10 px-6 text-center">
              <p className="text-xs text-slate-500 font-bold">
                {t("materials.recommendedEmpty")}
              </p>
            </div>
          }
        />
      </div>
      </div>

      <PeerExpertsRail experts={peerExperts} onRequestVisit={onRequestVisit} />
    </div>
  );
}
